import { FC, useEffect, useState } from "react";

import { Checkbox } from "shared/ui/shadcn/checkbox";
import { clsxMerge } from "shared/lib/helpers";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "shared/ui/shadcn/select";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";

interface FollowupSectionSettingsPropsI extends PropsWithClassNameI {
  isEnabled?: boolean;
  maxFollowups?: number;
  intervalDays?: number;
  onChange?: ({
    isEnabled,
    maxFollowups,
    intervalDays,
  }: {
    isEnabled: boolean;
    maxFollowups: number;
    intervalDays: number;
  }) => void;
}

const MAX_FOLLOWUP_OPTIONS = Array.from({ length: 6 }).map(
  (_, index) => `${index + 1}`
);

const FOLLOWUP_INTERVAL_DAYS = Array.from({ length: 7 }).map(
  (_, index) => `${index + 1}`
);

const DISABLED_OPTION = "0";

export const FollowupSectionSettings: FC<FollowupSectionSettingsPropsI> = ({
  className,
  isEnabled,
  maxFollowups,
  intervalDays,
  onChange,
}) => {
  const [isFollowupSettingsEnabled, setIsFollowupSettingsEnabled] = useState<
    boolean | undefined
  >(isEnabled);
  const [selectedMaxFollowupsValue, setSelectedMaxFollowupsValue] = useState(
    maxFollowups?.toString() || MAX_FOLLOWUP_OPTIONS[0]
  );
  const [selectedIntervalDaysValue, setSelectedIntervalDaysValue] = useState(
    intervalDays?.toString() || FOLLOWUP_INTERVAL_DAYS[0]
  );

  useEffect(() => {
    setIsFollowupSettingsEnabled(isEnabled);
  }, [isEnabled]);

  useEffect(() => {
    onChange?.({
      isEnabled: !!isFollowupSettingsEnabled,
      maxFollowups: parseInt(selectedMaxFollowupsValue),
      intervalDays: parseInt(selectedIntervalDaysValue),
    });
  }, [
    isFollowupSettingsEnabled,
    selectedMaxFollowupsValue,
    selectedIntervalDaysValue,
  ]);

  useEffect(() => {
    setSelectedMaxFollowupsValue(
      maxFollowups?.toString() || MAX_FOLLOWUP_OPTIONS[0]
    );
  }, [maxFollowups]);

  useEffect(() => {
    setSelectedIntervalDaysValue(
      intervalDays?.toString() || FOLLOWUP_INTERVAL_DAYS[0]
    );
  }, [intervalDays]);

  const handleChangeFollowupSettingsValue = (value: boolean) => {
    setIsFollowupSettingsEnabled(value);

    const maxFollowupsValue = value ? MAX_FOLLOWUP_OPTIONS[0] : DISABLED_OPTION;
    setSelectedMaxFollowupsValue(maxFollowupsValue);

    const maxIntervalValue = value
      ? FOLLOWUP_INTERVAL_DAYS[0]
      : DISABLED_OPTION;
    setSelectedIntervalDaysValue(maxIntervalValue);
  };

  const handleChangeMaxFollowups = (value: string) => {
    if (!MAX_FOLLOWUP_OPTIONS.includes(value)) {
      console.error("Invalid max followups value provided: ", value);
      return;
    }

    setSelectedMaxFollowupsValue(value);
  };

  const handleChangeIntervalDays = (value: string) => {
    if (!FOLLOWUP_INTERVAL_DAYS.includes(value)) {
      console.error("Invalid followup interval value provided: ", value);
      return;
    }

    setSelectedIntervalDaysValue(value);
  };

  return (
    <div className={clsxMerge("flex flex-col", className)}>
      <label className="flex cursor-pointer items-center gap-2 typography-body-4">
        <Checkbox
          checked={isFollowupSettingsEnabled}
          onCheckedChange={(state) =>
            handleChangeFollowupSettingsValue(!!state)
          }
        />
        Send a follow up message if the prospect does not reply?
      </label>

      <div
        className={clsxMerge(
          "mt-5 flex flex-col gap-2.5",
          !isFollowupSettingsEnabled && "pointer-events-none opacity-50"
        )}
      >
        <div className="flex flex-col gap-1">
          <span className="typography-body-4-medium">Select frequency</span>
          <span className="typography-body-4">
            How many follow up messages do you want to send:
          </span>

          <Select
            defaultValue={MAX_FOLLOWUP_OPTIONS[0]}
            value={selectedMaxFollowupsValue}
            onValueChange={handleChangeMaxFollowups}
          >
            <SelectTrigger className="mt-2 w-[113px] ring-offset-0 focus:outline-none focus:ring-0 focus:ring-transparent focus:ring-offset-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="w-[113px]">
              {MAX_FOLLOWUP_OPTIONS.map((value) => (
                <SelectItem key={value} value={value}>
                  {value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <span className="typography-body-4-medium">Select time</span>

          <div className="flex items-center gap-1">
            <Select
              defaultValue={FOLLOWUP_INTERVAL_DAYS[0]}
              value={selectedIntervalDaysValue}
              onValueChange={handleChangeIntervalDays}
            >
              <SelectTrigger className="h-[30px] w-[66px] ring-offset-0 focus:outline-none focus:ring-0 focus:ring-transparent focus:ring-offset-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="w-[30px]">
                {MAX_FOLLOWUP_OPTIONS.map((value) => (
                  <SelectItem key={value} value={value}>
                    {value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <span className="typography-body-4">days after the last email</span>
          </div>
        </div>
      </div>
    </div>
  );
};
